/**
 * The Spelling Bee Solver: every word a seven-letter hive allows.
 *
 * Rules, as the puzzle states them: a word has at least four letters, uses only the
 * hive's seven letters (any letter any number of times), and contains the centre letter.
 * A word that uses all seven letters is a pangram. Scoring: a four-letter word earns one
 * point, a longer word earns one point per letter, and a pangram earns seven more.
 *
 * Each word is reduced to a 26-bit mask of its letters, so "only the allowed letters"
 * is `mask & ~allowed === 0`, "has the centre" is `mask & centre`, and "pangram" is
 * `mask === allowed` — one pass over the list, no per-letter loop per word.
 */

// biome-ignore-start lint/suspicious/noBitwiseOperators: a letter set is a 26-bit mask, one bit per letter

export const MIN_BEE_LENGTH = 4;
export const PANGRAM_BONUS = 7;
export const OUTER_COUNT = 6;

const LETTER_A = 97;
const RE_SEPARATORS = /[\s,]+/g;
const RE_LETTERS = /^[a-z]*$/;

export interface BeePuzzle {
  center: string;
  /** The six outer letters, as typed (order kept for display). */
  outer: string[];
  /** Bitmask of all seven letters. */
  allowed: number;
  /** Bitmask of the centre letter. */
  centerBit: number;
}

export type BeeParse =
  | { ok: true; puzzle: BeePuzzle }
  | {
      ok: false;
      reason:
        | "empty"
        | "invalid"
        | "center-length"
        | "outer-count"
        | "duplicate";
    };

const bit = (letter: string) => 1 << (letter.charCodeAt(0) - LETTER_A);

/** Read the centre letter and the six outer letters; case, spaces and commas ignored. */
export function parsePuzzle(centerText: string, outerText: string): BeeParse {
  const center = centerText.toLowerCase().replace(RE_SEPARATORS, "");
  const outer = outerText.toLowerCase().replace(RE_SEPARATORS, "");
  if (!(center || outer)) {
    return { ok: false, reason: "empty" };
  }
  if (!(RE_LETTERS.test(center) && RE_LETTERS.test(outer))) {
    return { ok: false, reason: "invalid" };
  }
  if (center.length !== 1) {
    return { ok: false, reason: "center-length" };
  }
  if (outer.length !== OUTER_COUNT) {
    return { ok: false, reason: "outer-count" };
  }
  const letters = [center, ...outer];
  if (new Set(letters).size !== letters.length) {
    return { ok: false, reason: "duplicate" };
  }
  return {
    ok: true,
    puzzle: {
      center,
      outer: [...outer],
      allowed: letters.reduce((mask, letter) => mask | bit(letter), 0),
      centerBit: bit(center),
    },
  };
}

function maskOf(word: string): number {
  let mask = 0;
  for (let i = 0; i < word.length; i += 1) {
    mask |= 1 << (word.charCodeAt(i) - LETTER_A);
  }
  return mask;
}

/** Points for a word: 1 for four letters, else its length; pangrams earn 7 more. */
export function wordPoints(length: number, pangram: boolean): number {
  const base = length === MIN_BEE_LENGTH ? 1 : length;
  return pangram ? base + PANGRAM_BONUS : base;
}

export interface BeeWord {
  word: string;
  points: number;
  pangram: boolean;
}

export interface BeeResult {
  words: BeeWord[];
  points: number;
  pangrams: number;
}

/** Every valid word, in list (alphabetical) order, with its points. */
export function solveBee(
  list: readonly string[],
  puzzle: BeePuzzle
): BeeResult {
  const words: BeeWord[] = [];
  let points = 0;
  let pangrams = 0;
  for (const word of list) {
    if (word.length < MIN_BEE_LENGTH) {
      continue;
    }
    const mask = maskOf(word);
    if (mask & ~puzzle.allowed || !(mask & puzzle.centerBit)) {
      continue;
    }
    const pangram = mask === puzzle.allowed;
    const earned = wordPoints(word.length, pangram);
    words.push({ word, points: earned, pangram });
    points += earned;
    if (pangram) {
      pangrams += 1;
    }
  }
  return { words, points, pangrams };
}

export interface HintGrid {
  lengths: number[];
  rows: { letter: string; counts: number[]; total: number }[];
  /** Two-letter starts with how many words begin with them, alphabetical. */
  pairs: { prefix: string; count: number }[];
}

/** The spoiler-free hint grid: counts by first letter and length, plus two-letter starts. */
export function buildHints(words: readonly BeeWord[]): HintGrid {
  const lengths = [...new Set(words.map((entry) => entry.word.length))].sort(
    (a, b) => a - b
  );
  const byLetter = new Map<string, Map<number, number>>();
  const pairs = new Map<string, number>();
  for (const { word } of words) {
    const row = byLetter.get(word[0]) ?? new Map<number, number>();
    row.set(word.length, (row.get(word.length) ?? 0) + 1);
    byLetter.set(word[0], row);
    const prefix = word.slice(0, 2);
    pairs.set(prefix, (pairs.get(prefix) ?? 0) + 1);
  }
  return {
    lengths,
    rows: [...byLetter]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([letter, counts]) => ({
        letter,
        counts: lengths.map((length) => counts.get(length) ?? 0),
        total: [...counts.values()].reduce((sum, n) => sum + n, 0),
      })),
    pairs: [...pairs]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([prefix, count]) => ({ prefix, count })),
  };
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the mask code
